import { formatMoney } from "./currencyFormat";
import { normalizeTimelineItems } from "./timeline";

/** Map backend stock_status to card badge key (investor UI uses preorder without underscore). */
export function mapStockStatusForUi(stockStatus) {
  const s = String(stockStatus || "in_stock").toLowerCase();
  if (s === "pre_order") return "preorder";
  return s;
}

export function mapMarketplaceCategory(raw) {
  if (!raw) return null;
  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    description: raw.description || "",
    parentId: raw.parent_id ?? null,
    sortOrder: raw.sort_order ?? 0,
    productsCount: raw.products_count ?? 0,
    isActive: raw.is_active !== false,
  };
}

export function mapMarketplaceProductCard(raw) {
  if (!raw) return null;
  const stock = mapStockStatusForUi(raw.stock_status);
  return {
    id: raw.id,
    slug: raw.slug,
    name: raw.name,
    category: raw.category_name || "Product",
    categoryId: raw.category_id,
    description: raw.short_description || "",
    priceNgn: Number(raw.price) || 0,
    unit: raw.unit_label || "unit",
    stock,
    stockStatus: raw.stock_status,
    coverImage: raw.cover_image || null,
    deliveryAvailable: !!raw.delivery_available,
    pickupAvailable: !!raw.pickup_available,
    pickupLocation: raw.pickup_location || "",
    pickupLocations: Array.isArray(raw.pickup_locations)
      ? raw.pickup_locations.filter((l) => String(l || "").trim())
      : raw.pickup_location
        ? [raw.pickup_location]
        : [],
    preOrderAllowed: !!raw.pre_order_allowed,
    minimumQuantity: raw.minimum_quantity ?? 1,
    maximumQuantity: raw.maximum_quantity ?? null,
    onlinePaymentAvailable: raw.online_payment_available === true,
  };
}

export function mapMarketplaceProductDetail(raw) {
  if (!raw) return null;
  const card = mapMarketplaceProductCard(raw);
  const content = raw.public_content || {};
  const faqs = Array.isArray(raw.faqs)
    ? raw.faqs
    : Array.isArray(content.faqs)
      ? content.faqs
      : [];

  return {
    ...card,
    fullDescription: raw.full_description || "",
    sellerName: raw.seller_name || "",
    pickupLocation: raw.pickup_location || "",
    pickupLocations: Array.isArray(raw.pickup_locations)
      ? raw.pickup_locations.filter((l) => String(l || "").trim())
      : raw.pickup_location
        ? [raw.pickup_location]
        : [],
    deliveryRegions: raw.delivery_regions || content.deliveryRegions || [],
    estimatedFulfillmentTime: raw.estimated_fulfillment_time || "",
    media: raw.media || content.gallery || [],
    whatYouGet: raw.what_you_get || content.whatYouGet || content.what_you_get || [],
    qualityAssurance: raw.quality_assurance || content.qualityNote || content.quality_assurance || "",
    highlights: content.highlights || [],
    faqs: faqs.map((f) => ({
      question: f.question || f.q || "",
      answer: f.answer || f.a || "",
    })),
    canOrder: raw.can_order !== false,
    checkoutModesSupported: raw.checkout_modes_supported || ["book", "pay_online"],
    onlinePaymentAvailable: raw.online_payment_available === true,
    lifecycleStatus: raw.lifecycle_status,
    isPreview: !!raw.is_preview,
    stockQuantity: raw.stock_quantity,
  };
}

function paymentStatusLabel(status) {
  const map = {
    not_integrated: "Payment arrangement pending confirmation",
    pending: "Online payment pending",
    confirmed: "Paid",
    failed: "Payment failed",
    expired: "Payment expired",
    refunded: "Refunded",
  };
  return map[status] || status || "—";
}

function orderNextStep(raw) {
  const status = raw?.status;
  const paymentStatus = raw?.payment_status;
  const checkoutMode = raw?.checkout_mode;

  if (checkoutMode === "pay_online") {
    if (paymentStatus === "pending" || status === "pending_payment") {
      return "Complete payment to confirm your order.";
    }
    if (paymentStatus === "confirmed") {
      return "Payment received. Your order is confirmed.";
    }
    if (paymentStatus === "failed") {
      return "Payment could not be confirmed.";
    }
    if (paymentStatus === "expired") {
      return "Payment window expired and reserved stock was released.";
    }
  }

  const map = {
    submitted: "Your order has been received.",
    pending_payment: "Complete payment to confirm your order.",
    awaiting_confirmation: "Our team is confirming availability.",
    confirmed: "Your order has been confirmed.",
    processing: "Your order is being prepared.",
    ready_for_pickup: "Your order is ready for pickup.",
    shipped: "Your order is on the way.",
    delivered: "Delivery has been recorded.",
    completed: "Your order is complete.",
    declined: "This order could not be fulfilled.",
    cancelled: "This order has been cancelled.",
  };
  return map[status] || "";
}

export function mapMarketplaceOrder(raw) {
  if (!raw) return null;
  const items = (raw.items || []).map((item) => ({
    id: item.id,
    productId: item.product_id,
    productName: item.product_name,
    sku: item.sku,
    unitLabel: item.unit_label,
    quantity: item.quantity,
    unitPrice: Number(item.unit_price) || 0,
    lineTotal: Number(item.line_total) || 0,
  }));

  const timeline = normalizeTimelineItems(raw.timeline);

  return {
    id: raw.id,
    orderNumber: raw.order_number,
    type: "marketplace",
    status: raw.status,
    paymentStatus: raw.payment_status,
    paymentStatusLabel: paymentStatusLabel(raw.payment_status),
    checkoutMode: raw.checkout_mode,
    deliveryMethod: raw.delivery_method,
    subtotal: Number(raw.subtotal) || 0,
    deliveryFee: Number(raw.delivery_fee) || 0,
    totalAmount: Number(raw.total_amount) || 0,
    currency: raw.currency || "NGN",
    itemsCount: raw.items_count ?? items.length,
    items,
    fullName: raw.full_name,
    phone: raw.phone,
    email: raw.email,
    contactMethod: raw.contact_method,
    customerNote: raw.customer_note,
    deliveryAddress: raw.delivery_address,
    pickupLocation: raw.pickup_location,
    paymentUrl: raw.payment_url ?? null,
    paymentReference: raw.payment_reference ?? null,
    paymentAmount: Number(raw.payment_amount) || Number(raw.total_amount) || 0,
    paymentFee: Number(raw.payment_fee) || 0,
    paymentTotal: Number(raw.payment_total) || Number(raw.total_amount) || 0,
    paymentConfirmedAt: raw.payment_confirmed_at ?? null,
    paymentFailureReason: raw.payment_failure_reason ?? null,
    stockHoldActive: !!raw.stock_hold_active,
    stockHoldExpiresAt: raw.stock_hold_expires_at ?? null,
    feeBreakdown: raw.fee_breakdown || raw.fee_breakdown_json || null,
    onlinePaymentAvailable: raw.online_payment_available === true,
    submittedAt: raw.submitted_at,
    confirmedAt: raw.confirmed_at,
    completedAt: raw.completed_at,
    cancelledAt: raw.cancelled_at,
    timeline,
    nextStep: orderNextStep(raw),
    canCancel: ["submitted", "pending_payment", "awaiting_confirmation"].includes(raw.status),
    title: items[0]?.productName || `Order ${raw.order_number}`,
    // Shape compatible with InvestorRequestsList
    payload: {
      title: items[0]?.productName || `Order ${raw.order_number}`,
      quantity: items.reduce((sum, i) => sum + (i.quantity || 0), 0),
      totalNgn: Number(raw.total_amount) || 0,
      unit: items[0]?.unitLabel,
      phone: raw.phone,
    },
    reference: raw.order_number,
    createdAt: raw.submitted_at,
    created_at: raw.submitted_at,
  };
}

export function formatMarketplaceMoney(amount) {
  return formatMoney(Number(amount) || 0);
}
